import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ArrayHelper } from 'tiime-expert-utils';
import { PaginationData, PaginationRange } from 'tiime-material';

import { ThreadApiService } from '@api-services/thread-api.service';
import { ThreadCategory } from '@enums/thread-category.enum';
import { ThreadFilters } from '@interfaces/thread-filters';
import { ThreadInfosFilters } from '@interfaces/thread-infos-filters';
import { BusinessUser } from '@models/business-user';
import { Contributor } from '@models/contributor';
import { Message } from '@models/message';
import { Thread } from '@models/thread';
import { ThreadTag } from '@models/thread-tag';
import { ThreadsInfos } from '@models/threads-infos';

import { FacadeBase } from './facade.base';

@Injectable({
  providedIn: 'root'
})
export class ThreadFacade extends FacadeBase {
  constructor(private readonly threadApiService: ThreadApiService) {
    super();
  }

  getThreads(filters: ThreadFilters, companyId?: number): Observable<Thread[]> {
    return this.threadApiService.getUnpaginatedThreads(filters, companyId);
  }

  getPaginatedThreads(
    pagination: PaginationRange,
    search: string,
    filters: ThreadFilters,
    companyId?: number
  ): Observable<PaginationData<Thread>> {
    return this.threadApiService.getThreads(pagination, search, filters, companyId);
  }

  create(thread: Thread, companyId?: number): Observable<Thread> {
    return this.threadApiService.createThread(thread, companyId);
  }

  createMessage(thread: Thread, content: string, companyId?: number): Observable<Thread> {
    return this.threadApiService.createMessage(thread.id, content, companyId).pipe(
      map((message: Message) => ({
        ...thread,
        lastAnswerBy: ThreadCategory.accountant,
        messages: [...thread.messages, message],
        readByCurrentUser: true
      }))
    );
  }

  updateMessage(thread: Thread, messageId: number, content: string, companyId?: number): Observable<Thread> {
    return this.threadApiService.updateMessage(thread.id, messageId, content, companyId).pipe(
      map((updatedMessage: Message) => ({
        ...thread,
        messages: ArrayHelper.updateOne(thread.messages, updatedMessage)
      }))
    );
  }

  close(thread: Thread, closed: boolean, companyId?: number): Observable<Thread> {
    return this.threadApiService.closeThread(thread.id, closed, companyId).pipe(
      switchMap(() => this.getBusinessUser()),
      map((businessUser: BusinessUser) => ({
        ...thread,
        closedAt: closed ? new Date() : null,
        closedBy: closed ? businessUser : null
      }))
    );
  }

  archive(thread: Thread, archived: boolean, companyId?: number): Observable<Thread> {
    return this.threadApiService.archiveThread(thread.id, archived, companyId).pipe(
      switchMap(() => this.getBusinessUser()),
      map((businessUser: BusinessUser) => ({
        ...thread,
        closedAt: archived ? new Date() : thread.closedAt,
        closedBy: archived ? businessUser : thread.closedBy,
        archivedAt: archived ? new Date() : null,
        archivedBy: archived ? businessUser : null
      }))
    );
  }

  markAsRead(thread: Thread, companyId?: number): Observable<Thread> {
    return this.threadApiService
      .markThreadAsRead(thread.id, companyId)
      .pipe(map(() => ({ ...thread, readByCurrentUser: true })));
  }

  updateInterlocutors(thread: Thread, interlocutors: Contributor[], companyId?: number): Observable<Thread> {
    return this.threadApiService.updateThreadInterlocutors(thread.id, interlocutors, companyId);
  }

  updateTags(thread: Thread, tags: ThreadTag[], companyId?: number): Observable<Thread> {
    return this.threadApiService.updateThreadTags(thread.id, tags, companyId);
  }

  /**
   * Infos
   */

  getInfos(filters?: ThreadInfosFilters, companyId?: number): Observable<ThreadsInfos> {
    return this.threadApiService.getThreadsInfos(filters, companyId);
  }

  /**
   * Tags
   */

  getTags(companyId?: number): Observable<ThreadTag[]> {
    return this.threadApiService.getTags(companyId);
  }

  archiveTag(tag: ThreadTag, archived: boolean, companyId?: number): Observable<ThreadTag> {
    return this.threadApiService.archiveTag(tag.id, archived, companyId).pipe(
      switchMap(() => this.getBusinessUser()),
      map((businessUser: BusinessUser) => ({
        ...tag,
        archivedAt: archived ? new Date() : null,
        archivedBy: archived ? businessUser : null
      }))
    );
  }
}
